import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Logique centralisée des cotisations.
 *
 * Avant ce module, le filtrage par statut et les totaux étaient recalculés
 * à la main dans Overview, Finances, Members et Contributions — d'où des
 * divergences (ex: cotisations rejetées comptées dans le total global).
 * Tout passe désormais par ces helpers pour garantir la cohérence.
 */

export const STATUS = {
  CONFIRMED: 'confirmed',
  REJECTED: 'rejected',
  PENDING: 'pending'
};

export const isConfirmed = (c) => c?.status === STATUS.CONFIRMED;
export const isRejected = (c) => c?.status === STATUS.REJECTED;
// "En attente" = ni confirmée ni rejetée (statut absent ou 'pending')
export const isPending = (c) => !isConfirmed(c) && !isRejected(c);

/** Convertit un createdAt (Timestamp Firestore | ISO | Date) en Date JS. */
export const toDate = (c) => {
  const raw = c?.createdAt;
  if (raw?.toDate) return raw.toDate();
  if (raw) return new Date(raw);
  return new Date();
};

export const sumAmount = (list = []) => list.reduce((s, c) => s + (c.amount || 0), 0);
export const sumConfirmed = (list = []) => sumAmount(list.filter(isConfirmed));

// Une cotisation rattachée à un événement (collecte ponctuelle) ne compte pas
// dans l'objectif annuel du membre : c'est une contribution exceptionnelle.
export const isEventContribution = (c) => !!c?.eventId || c?.type === 'evenement';

export const inSameMonth = (c, ref = new Date()) => {
  const d = toDate(c);
  return d.getMonth() === ref.getMonth() && d.getFullYear() === ref.getFullYear();
};

export const inYear = (c, year = new Date().getFullYear()) => toDate(c).getFullYear() === year;

/**
 * Statistiques standard pour une liste de cotisations.
 * @returns {{ total, thisMonth, pendingCount, pendingAmount, confirmedCount }}
 */
export const computeStats = (list = [], ref = new Date()) => {
  const confirmed = list.filter(isConfirmed);
  const pending = list.filter(isPending);
  return {
    total: sumAmount(confirmed),
    // Total pris en compte dans l'objectif annuel : exclut les cotisations
    // rattachées à un événement (collectes ponctuelles).
    objectiveTotal: sumAmount(confirmed.filter((c) => !isEventContribution(c))),
    thisMonth: sumAmount(confirmed.filter((c) => inSameMonth(c, ref))),
    pendingCount: pending.length,
    pendingAmount: sumAmount(pending),
    confirmedCount: confirmed.length
  };
};

/**
 * Répartition mensuelle (12 cases) des cotisations VALIDÉES d'une année.
 * Index 0 = Janvier … 11 = Décembre.
 */
export const monthlyBreakdown = (list = [], year = new Date().getFullYear()) => {
  const months = Array(12).fill(0);
  list
    .filter((c) => isConfirmed(c) && inYear(c, year))
    .forEach((c) => {
      months[toDate(c).getMonth()] += c.amount || 0;
    });
  return months;
};

/** Total validé d'un membre pour une année donnée. */
export const memberAnnualTotal = (list = [], userId, year = new Date().getFullYear()) =>
  sumAmount(list.filter((c) => c.userId === userId && isConfirmed(c) && inYear(c, year)));

export const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

// ---- Helpers de récupération Firestore ----

// NB : les cotisations "supprimées" (corbeille, deleted:true) sont exclues de
// toutes les agrégations. Seule la page Finances les lit (pour la corbeille).
export const fetchAllContributions = async () => {
  const snap = await getDocs(query(collection(db, 'contributions'), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => !c.deleted);
};

export const fetchUserContributions = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'contributions'), where('userId', '==', userId), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => !c.deleted);
};
